import type { FC } from "react";
import { useMemo, useState, useRef, useCallback } from "react";

import dayjs from "dayjs";
import type { Point, SeriesLineOptions } from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";

import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex, Stack } from "@similarweb/ui-components/foundations";

import { toggleItems } from "app/components/widget/widget-utilities/time-granularity";
import { swSettings } from "app/common/services/swSettings";
import { VerticalLegendsWithSearch } from "app/components/React/legend/vertical-legends-with-search/VerticalLegendsWithSearch";
import type { ILegendItem } from "app/components/React/Legends/legendsTypes";
import { CHART_COLORS } from "app/constants/ChartColors";
import { useColorStack } from "app/custom-hooks/useColorStack";
import { translate } from "app/filters/i18nFilter";
import type { IPeriodOverPeriodCompareType } from "app/services/periodOverPeriodTypes";
import { allTrackers } from "app/services/track/track";

import { chartOptions, DemandAnalysisLineChart } from "../../../components/DemandAnalysisLineChart";
import { formatNumber } from "../../../utils/numberFormatters";
import { getSecondaryColorPattern } from "../../keywords-trends-widget/helpers";
import { SearchVolumeOverTimeChartTooltip } from "../../search-volume-over-time-widget/SearchVolumeOverTimeChartTooltip";
import { ClusterLegendItemHint } from "../common/ClusterLegendItemHint";

import type { ILineChartSeriesData, IBaseLegendItem, ChartTooltipData } from "./demandOverTimeSelectors";
import { MAX_SELECTION, INITIAL_SELECTION, DEFAULT_UN_CHECK_COLOR, TOTAL_KEYWORDS_ID } from "./utils/const";

interface IDemandOverTimeChartProps {
    lineChartData: ILineChartSeriesData[];
    categories: string[];
    chartTooltipData: ChartTooltipData;
    periodOverPeriodType: IPeriodOverPeriodCompareType | null;
    isWindow: boolean;
    legendItems: IBaseLegendItem[];
    totalLegend: IBaseLegendItem | undefined;
    isCluster: boolean;
    duration?: string;
}

export const DemandOverTimeChart: FC<IDemandOverTimeChartProps> = ({
    lineChartData,
    categories,
    chartTooltipData,
    periodOverPeriodType,
    isWindow,
    legendItems,
    totalLegend,
    isCluster,
    duration,
}) => {
    const colorStack = useColorStack(CHART_COLORS.chartMainColors);
    const [visibleItems, setVisibleItems] = useState(
        () => new Map(legendItems.slice(0, INITIAL_SELECTION).map((item) => [item.id, colorStack.acquire()])),
    );
    const activePointRef = useRef<Point>(null);

    const updatedLegendItems = useMemo(() => {
        return legendItems.map((item): ILegendItem => {
            const isVisible = visibleItems.has(item.id);
            const tooltip = isCluster && item.children ? ClusterLegendItemHint({ keywords: item.children }) : undefined;

            return {
                id: item.id,
                name: item.name,
                data: formatNumber(item.value),
                visible: isVisible,
                color: isVisible ? visibleItems.get(item.id) : DEFAULT_UN_CHECK_COLOR,
                tooltip,
            };
        });
    }, [legendItems, visibleItems, isCluster]);

    const transformedTotalLegend: ILegendItem | undefined = useMemo(() => {
        if (!totalLegend) {
            return undefined;
        }
        return {
            id: totalLegend.id,
            name: totalLegend.name,
            data: formatNumber(totalLegend.value),
            visible: visibleItems.has(TOTAL_KEYWORDS_ID),
            color: visibleItems.has(TOTAL_KEYWORDS_ID) ? visibleItems.get(TOTAL_KEYWORDS_ID) : DEFAULT_UN_CHECK_COLOR,
        };
    }, [totalLegend, visibleItems]);

    const text = {
        searchPlaceHolder: translate(
            isCluster ? "common.placeholder.search.clusters" : "common.placeholder.search.keywords",
        ),
        searchLabel: translate(isCluster ? "common.clusters.selected" : "common.keywords.selected"),
    };

    const handleLegendToggle = (clickedItem: ILegendItem) => {
        const newVisibleItems = new Map(visibleItems);
        const isTotal = clickedItem.id === TOTAL_KEYWORDS_ID;

        if (isTotal) {
            if (clickedItem.visible) {
                newVisibleItems.delete(TOTAL_KEYWORDS_ID);
            } else {
                newVisibleItems.set(TOTAL_KEYWORDS_ID, colorsPalettes.carbon[100]);
            }
        } else if (clickedItem.visible && clickedItem.id && clickedItem.color) {
            colorStack.release(clickedItem.color);
            newVisibleItems.delete(clickedItem.id);
        } else if (clickedItem.id) {
            const newColor = colorStack.acquire();
            if (newColor) {
                newVisibleItems.set(clickedItem.id, newColor);
            }
        }

        setVisibleItems(newVisibleItems);

        allTrackers.trackEvent(
            "Click Filter",
            clickedItem.visible ? "uncheck" : "check",
            `Absolute/${clickedItem.name}`,
        );
    };

    const timeGranularityConfig = toggleItems[isWindow ? "Daily" : "Monthly"];

    const graphConfig = chartOptions({
        xAxis: {
            categories,
            labels: {
                formatter() {
                    return dayjs.utc(this.value as string).format(timeGranularityConfig.format);
                },
            },
        },
        tooltip: {
            formatter() {
                const selectedDateIndex = this.x;
                const id = activePointRef.current?.options.custom?.id as unknown;

                if (chartTooltipData === undefined || id === undefined || typeof id !== "string") {
                    return null;
                }

                const entry = chartTooltipData.isPeriodOverPeriod
                    ? chartTooltipData.data[id]?.[selectedDateIndex]
                    : chartTooltipData.data[selectedDateIndex];

                if (!entry) {
                    return null;
                }

                const tooltipDataItem = {
                    ...entry,
                    rows: entry.rows
                        .filter((row) => visibleItems.has(row.id))
                        .map((row) => ({ ...row, label: { ...row.label, color: visibleItems.get(row.id) } })),
                };

                return renderToStaticMarkup(
                    <SearchVolumeOverTimeChartTooltip
                        tooltipDataItem={tooltipDataItem}
                        untilDate={swSettings.current.windowEndDate}
                        periodOverPeriodType={periodOverPeriodType}
                        isDaily={!!isWindow}
                        isCluster={isCluster}
                        duration={duration}
                    />,
                );
            },
        },
    });

    const onPointMouseOver = useCallback((point: Point) => {
        activePointRef.current = point;
    }, []);

    const onPointMouseOut = useCallback(() => {
        activePointRef.current = null;
    }, []);

    const series = useMemo(
        () =>
            lineChartData
                .filter((lineChartDataItem) => visibleItems.has(lineChartDataItem.id))
                .map((lineChartDataItem, index) => {
                    const color = visibleItems.get(lineChartDataItem.id) ?? DEFAULT_UN_CHECK_COLOR;
                    const data = lineChartDataItem.data.map((pointData) => ({
                        ...pointData,
                        events: {
                            mouseOver(this: Point) {
                                onPointMouseOver(this);
                            },
                            mouseOut(this: Point) {
                                onPointMouseOut();
                            },
                        },
                    }));

                    return {
                        id: `${String(lineChartDataItem.name)}-${index}`,
                        name: lineChartDataItem.name,
                        color,
                        type: "line",
                        visible: true,
                        data,
                        dashStyle: lineChartDataItem.isPeriodOverPeriod ? "LongDash" : "Solid",
                        zoneAxis: "x",
                        zones:
                            lineChartDataItem.monthToDateIndex > 0
                                ? [{ value: lineChartDataItem.monthToDateIndex - 1 }, { dashStyle: "Dot" }]
                                : undefined,
                        marker: {
                            enabled: lineChartDataItem.data.length === 1,
                            fillColor:
                                lineChartDataItem.data.length === 1 && lineChartDataItem.isPeriodOverPeriod
                                    ? getSecondaryColorPattern(color)
                                    : color,
                        },
                    } satisfies SeriesLineOptions;
                }),
        [lineChartData, visibleItems, periodOverPeriodType, onPointMouseOver, onPointMouseOut],
    );

    return (
        <Flex
            width="100%"
            gap="24px"
        >
            <Box flex="1">
                <div data-automation="keyword-trends-graph-line">
                    <DemandAnalysisLineChart
                        options={graphConfig}
                        series={series}
                    />
                </div>
            </Box>
            <Stack width="235px">
                <VerticalLegendsWithSearch
                    legendItems={updatedLegendItems}
                    toggleSeries={handleLegendToggle}
                    maxSelection={MAX_SELECTION}
                    legendItemsHeight="200px"
                    text={text}
                    titles={[
                        translate(isCluster ? "common.clusters" : "common.keywords"),
                        translate("demand.analysis.common.avg_volume"),
                    ]}
                    otherLegend={transformedTotalLegend}
                    textMaxWidth="120px"
                    subtitleMaxWidth="90px"
                    limitationReachedTooltip={
                        isCluster
                            ? "demand.analysis.search.trends.demand.over.time.limitation.reached.clusters.tooltip"
                            : "demand.analysis.search.trends.demand.over.time.limitation.reached.tooltip"
                    }
                />
            </Stack>
        </Flex>
    );
};
